import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  AppState,
  PaymentTokenSlice,
  PaymentTokenRequestPayload,
} from '../interfaces/store.interface';
import { requestHandler } from '../utils/requestHandler';
import {
  isMock,
  setErrorInAppState,
  parseJson,
  paymentTokenBusinessErrorCheck,
} from '../utils/helpers';
import { mockPaymentTokenResponse } from '../mocks/mockPaymentTokenResponse';
import {
  PAYMENT_TOKEN_ENDPOINT,
  PAYMENT_TOKEN_CONTEXT,
  X_SYF_ID_TOKEN,
  X_SYF_MERCHANT_ID,
} from '../utils/constants/apiConstants';

export const initialState: PaymentTokenSlice = {
  featureToShow: '',
  // selectedAccount is used for all features EXCEPT addToWallet
  // If accordion is open then account is considered as selectedAccount
  selectedAccount: {
    accountId: '',
    code: '',
    encodedBarcode: '',
    productId: '',
    last4Accountnumber: '',
    tokenNumber: '',
    tokenExpiryDate: '',
    tokenCvv: '',
    isGenerateCardLoading: false,
    errorWhileFetchingToken: false,
    maxAttemptsCompleted: false,
    accountNotActivated: false,
  },
  // tokenGeneratedAccounts can be used for all features if required
  tokenGeneratedAccounts: [],
};

export const paymentToken = 'paymentToken';

const extractAccountIdFromPayload = (payload: any): string => {
  let responseAccountId = '';
  try {
    const reqPayload: any = parseJson(payload);
    responseAccountId = reqPayload?.accountId;
  } catch (error) {
    console.log('Error while parsing responseAccountId');
    responseAccountId = '';
  }
  return responseAccountId;
};

export const fetchPaymentToken = createAsyncThunk(
  `${paymentToken}/payment-token`,
  async (
    requestPayload: PaymentTokenRequestPayload,
    { getState, rejectWithValue },
  ) => {
    const state: any = getState();
    const transmitToken = state.customerVerificationForm.transmitToken;
    const clientId = requestPayload.selectedClient;
    const requestHeaders = {
      customHeaders: {
        [X_SYF_ID_TOKEN]: transmitToken,
        [X_SYF_MERCHANT_ID]: clientId,
      },
    };
    try {
      if (isMock()) {
        return mockPaymentTokenResponse;
      } else {
        const axiosInstance = requestHandler({
          ...requestHeaders,
        });
        const response = await axiosInstance.post(
          PAYMENT_TOKEN_ENDPOINT,
          requestPayload,
        );
        const responseAccountId = extractAccountIdFromPayload(
          response.config?.data,
        );
        // data massaging for better variable naming convention handling on UI side
        // renaming cipher.tokenNumber to tokenNumber etc
        const tokenNumber = response.data['cipher.tokenNumber'];
        const tokenExpiryDate = response.data['cipher.tokenExpiryDate'];
        const tokenCvv = response.data['cipher.tokenCvv'];
        const { code, encodedBarcode, productId, last4Accountnumber } =
          response.data;
        return {
          data: {
            code,
            encodedBarcode,
            productId,
            last4Accountnumber,
            tokenNumber,
            tokenExpiryDate,
            tokenCvv,
          },
          responseAccountId,
        };
      }
    } catch (error: any) {
      const responseAccountId = extractAccountIdFromPayload(
        error.response.config?.data,
      );
      const errorObject = error?.response || error;
      /**
       * 3rd param to setErrorInAppState is passed false
       * as navigation to error page is not required in this case
       * */
      setErrorInAppState(errorObject, PAYMENT_TOKEN_CONTEXT, false);
      return rejectWithValue({ data: error, responseAccountId });
    }
  },
);

export const paymentTokenSlice = createSlice({
  name: paymentToken,
  initialState,
  reducers: {
    setFeatureToShow: (state, action) => {
      state.featureToShow = action.payload;
    },
    setSelectedAccountDetails: (state, { payload }) => {
      const { accountId } = payload;
      const selectedAccount = state.tokenGeneratedAccounts.find(
        (singleAccount: any) => singleAccount.accountId === accountId,
      ) || {
        ...initialState.selectedAccount,
        accountId,
      };
      state.selectedAccount = {
        ...state.selectedAccount,
        ...selectedAccount,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentToken.pending, (state) => {
        state.selectedAccount = {
          ...state.selectedAccount,
          isGenerateCardLoading: true,
        };
      })
      .addCase(fetchPaymentToken.fulfilled, (state, action: any) => {
        const { responseAccountId, data } = action.payload;
        /** Step 1: Check if account response coming back is for the selected account */
        const isAccountSelected =
          state.selectedAccount.accountId === responseAccountId;

        /** Step 2: If response is for selected account/account on screen do this */
        // isAccountSelected: If selected account is NOT switched by the user before the response comes back then update selected account
        if (isAccountSelected) {
          state.selectedAccount = {
            ...state.selectedAccount,
            isGenerateCardLoading: false,
            errorWhileFetchingToken: false,
            maxAttemptsCompleted: false,
            accountNotActivated: false,
            accountId: responseAccountId, // accountId is made sure to be for the correct request
            ...data, // success response is spread here
          };
        }
        // else do not update the account
        // and the state.selectedAccount remains as whatever the state it was in before this change

        /** Step 3: Start process to see if this account was used BEFORE in the SAME session to generate token */
        // Check if the incoming response accountId is already been tried inside the session
        // if accountAlreadyTriedInSession has any data that means it was already tried,
        // which means need to update the existing entry in the array
        // if accountAlreadyTriedInSession has no data then add a new entry in the array
        let accountAlreadyTriedInSession = state.tokenGeneratedAccounts.filter(
          (account: any) => account.accountId === responseAccountId,
        );
        /** Step 3.1: accountAlreadyTriedInSession should have 1 entry if it was tried before if not set to defaults like below */
        if (accountAlreadyTriedInSession.length < 1) {
          accountAlreadyTriedInSession = [
            {
              ...initialState.selectedAccount,
              accountId: responseAccountId,
            },
          ];
        }

        /** Step 3.2: isTokenAlreadyGenerated any of the below 3 conditions are true then token was already generated
         * This step may not be necessary as step 3.1 already is checking it but due to time constraints this extra check
         * is left as is. Can be removed if deemed surplus during testing.
         */
        /** Shopping pass are issued with encoded barcode.
         * Check added if encodedBarcode is there
         */
        const isTokenAlreadyGenerated =
          accountAlreadyTriedInSession[0].errorWhileFetchingToken ||
          accountAlreadyTriedInSession[0].maxAttemptsCompleted ||
          accountAlreadyTriedInSession[0].tokenNumber !== '' ||
          accountAlreadyTriedInSession[0].encodedBarcode !== '' ||
          accountAlreadyTriedInSession[0].accountNotActivated;

        /** Step 4: If token already generated update array entry */
        if (isTokenAlreadyGenerated) {
          /**
           * token being generated AGAIN for this accountId in the same session
           * below approach is necessary to perform update existing state
           * WITHOUT mutation
           *  */
          state.tokenGeneratedAccounts = [
            ...state.tokenGeneratedAccounts.map((account: any) => {
              if (account.accountId === responseAccountId) {
                return {
                  ...account,
                  isGenerateCardLoading: false,
                  errorWhileFetchingToken: false,
                  maxAttemptsCompleted: false,
                  accountNotActivated: false,
                  ...data,
                };
              } else {
                return account;
              }
            }),
          ];
        } else {
          /** Step 5: If token already NOT generated add a new array entry */
          /**
           * token being generated for FIRST TIME for this accountId in the session
           * below approach is necessary to perform update existing state
           * WITHOUT mutation
           *  */
          state.tokenGeneratedAccounts = [
            ...state.tokenGeneratedAccounts,
            {
              isGenerateCardLoading: false,
              errorWhileFetchingToken: false,
              maxAttemptsCompleted: false,
              accountNotActivated: false,
              accountId: responseAccountId,
              ...data, // token endpoint response
            },
          ];
        }
      })
      /** Step 6: fetchPaymentToken.fulfilled section is now completed above */
      .addCase(fetchPaymentToken.rejected, (state, action: any) => {
        const { responseAccountId, data } = action.payload;
        /** Step 1: Check if account response coming back is for the selected account */
        const isAccountSelected =
          state.selectedAccount.accountId === responseAccountId;

        /** Step 2: Check if response has error or businessErrors as this is rejected section */
        const { maxAttemptsStatusCheck, accountNotActivatedCheck } =
          paymentTokenBusinessErrorCheck(data.response);

        /** Step 3: If response is for selected account/account on screen do this */
        /**
         * If maxAttemptsStatusCheck is true, meaning max attempts reached then set
         * maxAttemptsCompleted flag to true and
         * errorWhileFetchingToken to false (negation of( maxAttemptsStatusCheck and accountNotActivatedCheck))
         * but in case maxAttemptsStatusCheck is false, then check
         * If accountNotActivatedCheck is true, meaning account is not ready to use yet then set
         * accountNotActivated flag to true and
         *  errorWhileFetchingToken to false but in case accountNotActivated is also false then set
         * maxAttemptsCompleted flag as false (value of maxAttemptsStatusCheck),
         * accountNotActivated flag as false(value of accountNotActivatedCheck) and
         * errorWhileFetchingToken is true (negation of maxAttemptsStatusCheck and negation of accountNotActivatedCheck which is true)
         * making sure in any case both flags will have proper values
         * and are mutually exclusive
         */
        // isAccountSelected: If selected account is NOT switched by the user before the response comes back then update selected account
        if (isAccountSelected) {
          state.selectedAccount = {
            ...initialState.selectedAccount, // reset to initial state except for below values
            errorWhileFetchingToken:
              !maxAttemptsStatusCheck && !accountNotActivatedCheck,
            maxAttemptsCompleted: maxAttemptsStatusCheck,
            accountNotActivated: accountNotActivatedCheck,
            accountId: responseAccountId,
          };
        }
        // else do not update the account
        // and the state.selectedAccount remains as whatever the state it was in before this change

        /** Step 4: Start process to see if this account was used BEFORE in the SAME session to generate token */
        // Check if the incoming response accountId is already been tried inside the session
        // if accountAlreadyTriedInSession has any data that means it was already tried,
        // which means need to update the existing entry in the array
        // if accountAlreadyTriedInSession has no data then add a new entry in the array
        let accountAlreadyTriedInSession = state.tokenGeneratedAccounts.filter(
          (account: any) => account.accountId === responseAccountId,
        );
        /** Step 4.1: accountAlreadyTriedInSession should have 1 entry if it was tried before if not set to defaults like below */
        if (accountAlreadyTriedInSession.length < 1) {
          accountAlreadyTriedInSession = [
            {
              // if account not present, selected account is not generated for before so use default values
              // except for accountId
              ...initialState.selectedAccount,
              errorWhileFetchingToken:
                !maxAttemptsStatusCheck && !accountNotActivatedCheck,
              maxAttemptsCompleted: maxAttemptsStatusCheck,
              accountNotActivated: accountNotActivatedCheck,
              accountId: responseAccountId,
            },
          ];
        }

        /** Step 4.2: isTokenAlreadyGenerated any of the below 3 conditions are true then token was already generated
         * This step may not be necessary as step 3.1 already is checking it but due to time constraints this extra check
         * is left as is. Can be removed if deemed surplus during testing.
         */
        const isTokenAlreadyGenerated =
          accountAlreadyTriedInSession[0].errorWhileFetchingToken ||
          accountAlreadyTriedInSession[0].maxAttemptsCompleted ||
          accountAlreadyTriedInSession[0].tokenNumber !== '' ||
          accountAlreadyTriedInSession[0].encodedBarcode !== '' ||
          accountAlreadyTriedInSession[0].accountNotActivated;

        /** Step 5: If token already generated update array entry */
        if (isTokenAlreadyGenerated) {
          /**
           * token being generated AGAIN for this accountId in the same session
           * below approach is necessary to perform update existing state
           * WITHOUT mutation
           *  */
          state.tokenGeneratedAccounts = [
            ...state.tokenGeneratedAccounts.map((account: any) => {
              if (account.accountId === responseAccountId) {
                return {
                  ...account,
                  isGenerateCardLoading: false,
                  errorWhileFetchingToken:
                    !maxAttemptsStatusCheck && !accountNotActivatedCheck,
                  maxAttemptsCompleted: maxAttemptsStatusCheck,
                  accountNotActivated: accountNotActivatedCheck,
                  ...data,
                };
              } else {
                return account;
              }
            }),
          ];
        } else {
          /** Step 6: If token already NOT generated add a new array entry */
          /**
           * token being generated for FIRST TIME for this accountId in the session
           * below approach is necessary to perform update existing state
           * WITHOUT mutation
           *  */
          state.tokenGeneratedAccounts = [
            ...state.tokenGeneratedAccounts,
            {
              ...state.selectedAccount,
            },
          ];
        }
      });
    /** Step 7: fetchPaymentToken.rejected section is now completed above */
  },
});

// state actions can be exported here
export const { setFeatureToShow, setSelectedAccountDetails } =
  paymentTokenSlice.actions;

export default paymentTokenSlice.reducer;

// state getters can be written & exported here
export const getSelectedTokenAccount = (state: AppState) =>
  state.paymentToken.selectedAccount;
export const getFeatureToShow = (state: AppState) =>
  state.paymentToken.featureToShow;
